// Hook to fetch the /appLog data

/*  Shared  */
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { AppLog } from 'application/Shared/types/appLog';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useEffect, useState } from 'react';

type FetchTenantApplicationLogsResult = {
    loading: boolean;
    logs: AppLog[];
    logsExist: boolean;
    refetchLogs: () => void;
};

/**
 * Custom hook to fetch tenant application logs from the /appLog endpoint
 * @param {Object} params - The parameters object
 * @param {string} [params.applicationId] - Optional application ID to filter logs
 * @param {string} [params.clientId] - Optional client/tenant ID to filter logs
 * @param {string} [params.userId] - Optional user ID to filter logs
 * @returns {Object} Returns object containing:
 *   - loading {boolean} - Loading state of the request
 *   - logs {Array} - Array of log entries
 *   - logsExist {boolean} - Whether any logs were found
 *   - refetchLogs {Function} - Function to trigger a manual refetch of logs
 */
export const useFetchTenantApplicationLogs = ({
    applicationId,
    clientId,
    userId
}: {
    applicationId?: string | null;
    clientId?: string;
    userId?: string | null;
}): FetchTenantApplicationLogsResult => {
    const [logs, setLogs] = useState([]);
    const [logsExist, setLogsExist] = useState(false);
    const [loading, setLoading] = useState(true);
    // calling refetchLogs will force a rerender on the hook
    const [shouldRefetchLogs, setShouldRefetchLogs] = useState({});

    const refetchLogs = () => setShouldRefetchLogs({});

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const params: {
            platformTenantId?: string;
            includeClients?: boolean;
            applicationId?: string;
            platformUserId?: string;
            size: number;
        } = { size: -1 };

        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        if (applicationId) {
            params.applicationId = applicationId;
        }

        if (userId) {
            params.platformUserId = userId;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/appLog', {
                    cancelToken: source.token,
                    params
                });
                if (Response.hasData(response)) {
                    setLogs(Response.getData(response));
                    setLogsExist(true);
                } else {
                    setLogs([]);
                    setLogsExist(false);
                }
            } catch (error) {
                debug.e('Error Getting Logs', error);
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [applicationId, clientId, shouldRefetchLogs, userId]);

    return {
        loading,
        logs,
        logsExist,
        // returning the refetchLogs function so we can call it as needed.
        refetchLogs
    };
};
